import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { cardBackground, cardBorder } from '../styles/themeHelpers';
import { verifyResetCode } from '../services/passwordResetService';
import AnimatedBackground from '../components/common/AnimatedBackground';

const Background = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients?.background || theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const CenterBlock = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
`;

const AccessFrame = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 2px solid ${({ theme }) => cardBorder(theme)};
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
  padding: 30px;
  border-radius: 12px;
  max-width: 420px;
  width: 100%;
  position: relative;
  text-align: center;
  z-index: 10;
  backdrop-filter: none;

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const AccessTitle = styled.h1`
  font-size: 1.5rem;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 10px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
`;

const SubStatus = styled.div`
  color: #b0b0b0;
  font-size: 13px;
  margin-bottom: 25px;
  letter-spacing: 1px;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const StepDot = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props =>
    props.$completed ? '#4caf50' :
    props.$active ? '#00d4ff' :
    'rgba(255, 255, 255, 0.2)'};
  transition: all 0.3s ease;
`;

const InputBlock = styled.div`
  margin-bottom: 15px;
`;

const InputLabel = styled.div`
  color: #00d4ff;
  font-size: 13px;
  margin-bottom: 8px;
  letter-spacing: 1px;
  text-align: left;
  font-weight: 500;
`;

const StatusText = styled.div`
  color: #b0b0b0;
  font-size: 11px;
  margin-top: 8px;
  letter-spacing: 1px;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 12px 0;
  margin: 20px 0;
  cursor: pointer;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #00a8cc, #007799);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-top: 15px;
`;

const FrameLink = styled(Link)`
  color: #00d4ff;
  font-size: 12px;
  text-decoration: none;
  letter-spacing: 1px;
  padding: 6px 10px;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    border-color: rgba(0, 212, 255, 0.5);
    background: rgba(0, 212, 255, 0.1);
  }
`;

const ErrorMsg = styled.div`
  color: #ff4444;
  font-size: 13px;
  text-align: center;
  margin-bottom: 15px;
  padding: 10px;
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  border-radius: 6px;
`;

const SuccessMsg = styled.div`
  color: #4caf50;
  font-size: 14px;
  text-align: center;
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
`;

const CodeInput = styled.input`
  width: 45px;
  height: 55px;
  margin: 0 4px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 24px;
  text-align: center;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: #00d4ff;
    background: rgba(0, 212, 255, 0.1);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }

  @media (max-width: 400px) {
    width: 40px;
    height: 50px;
    font-size: 20px;
  }
`;

const CodeInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 20px 0;
`;

const EmailText = styled.div`
  color: #00d4ff;
  font-size: 14px;
  margin-bottom: 15px;
  word-break: break-all;
  background: rgba(0, 212, 255, 0.1);
  padding: 10px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 6px;
`;

const InfoMessage = styled.div`
  color: #4caf50;
  font-size: 13px;
  text-align: center;
  margin-bottom: 20px;
  padding: 12px;
  border: 1px solid rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.1);
  border-radius: 6px;
`;

const ResetPasswordCodePage: React.FC = () => {
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  useEffect(() => {
    const storedEmail = sessionStorage.getItem('resetEmail');
    const storedMessage = sessionStorage.getItem('resetMessage');
    if (!storedEmail) {
      navigate('/forgot-password');
      return;
    }
    setEmail(storedEmail);
    if (storedMessage) {
      setInfoMessage(storedMessage);
      sessionStorage.removeItem('resetMessage');
    }
  }, [navigate]);

  const handleChange = (index: number, value: string) => {
    // Если вставили полный код (6 цифр)
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      const digits = value.split('');
      setCode(digits);
      inputRefs.current[5]?.focus();
      return;
    }

    if (value && !/^\d*$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value.slice(0, 1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Извлекаем только цифры
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < digits.length && i < 6; i++) {
        newCode[i] = digits[i];
      }
      setCode(newCode);

      // Фокус на последнюю заполненную или следующую пустую ячейку
      const focusIndex = Math.min(digits.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (code[index] === '' && index > 0) {
        const newCode = [...code];
        newCode[index - 1] = '';
        setCode(newCode);
        inputRefs.current[index - 1]?.focus();
      } else if (code[index] !== '') {
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (code.some(digit => digit === '')) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    const fullCode = code.join('');

    try {
      const response = await verifyResetCode(email, fullCode);

      if (response.success) {
        setSuccess(true);
        sessionStorage.setItem('resetCode', fullCode);

        setTimeout(() => {
          navigate('/reset-password-new');
        }, 1500);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Error verifying code. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Background>
      <AnimatedBackground />

      <CenterBlock>
        <AccessFrame>
          <AccessTitle>Verify Code</AccessTitle>
          <SubStatus>Step 2 of 3</SubStatus>

          <StepIndicator>
            <StepDot $completed />
            <StepDot $active />
            <StepDot />
          </StepIndicator>

          {infoMessage && <InfoMessage>{infoMessage}</InfoMessage>}

          {success ? (
            <SuccessMsg>
              Code verified successfully!
              <br />
              <span style={{ fontSize: '12px', marginTop: '10px', display: 'block' }}>
                Proceeding to set new password...
              </span>
            </SuccessMsg>
          ) : (
            <form onSubmit={handleSubmit} autoComplete="off">
              {error && <ErrorMsg>{error}</ErrorMsg>}

              <InputBlock>
                <InputLabel>Enter code sent to:</InputLabel>
                <EmailText>{email}</EmailText>

                <CodeInputContainer>
                  {Array(6).fill(0).map((_, index) => (
                    <CodeInput
                      key={index}
                      ref={el => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={code[index]}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      autoFocus={index === 0}
                    />
                  ))}
                </CodeInputContainer>

                <StatusText>Code is valid for 1 hour</StatusText>
              </InputBlock>

              <SubmitButton
                type="submit"
                disabled={loading || code.some(digit => digit === '')}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </SubmitButton>
            </form>
          )}

          <LinkRow>
            <FrameLink to="/forgot-password">Change Email</FrameLink>
            <FrameLink to="/login">Back to Login</FrameLink>
          </LinkRow>
        </AccessFrame>
      </CenterBlock>
    </Background>
  );
};

export default ResetPasswordCodePage;
